import { logError } from '@databases/logs';
import { TranslatableError } from '@databases/utils/errors';
import { generateId } from '@databases/utils/utils';

import { addFlag } from './flags';

export interface ErrorSettings {
  /** If specified, opening a flag with this ID will clear any previous flags with the same ID. */
  id?: string;
  title?: string;
}

declare global {
  interface Error {
    _orderlyFlagHandled?: boolean;
  }
}

/**
 * Renders a dismissible error flag containing the message of the given error object.
 */
function handleError(
  error: Error,
  { id = generateId(), title = 'Error' }: ErrorSettings = {}
): void {
  // Avoid showing multiple flags for the same error. This might happen when a callback wrapped in useFlaggingCallback
  // is passed as a prop that is called in another callback wrapped in useFlaggingCallback.
  if (error._orderlyFlagHandled) {
    return;
  }
  error._orderlyFlagHandled = true;

  const bodyI18nId = error instanceof TranslatableError ? error.i18nId : undefined;
  const i18nVariables =
    error instanceof TranslatableError ? error.i18nVariables : undefined;

  addFlag({
    id,
    type: 'error',
    title,
    body: error.message,
    bodyI18nId,
    i18nVariables,
  });

  logError(error);
}

/**
 * Calls the given callback and raises a flag in case a sync or async exception is thrown. The exception is not
 * caught.
 */
export function flagErrors<T>(callback: () => T, settings?: ErrorSettings): T {
  try {
    const result = callback();
    Promise.resolve(result).catch((error) => {
      handleError(error, settings);
      throw error;
    });
    return result;
  } catch (error: unknown) {
    if (error instanceof Error) {
      handleError(error, settings);
    }
    throw error;
  }
}
